import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  FormSection,
  Select,
  Textfield,
} from '@zolteam/zol-admin-ui';
import { useMutation } from '@tanstack/react-query';
// utils
import { BOOKING_DOC_TYPES } from '../../../domain/bookingDocuments/bookingDocumentsUseCases';
// components
import UploadFile from '../../atoms/uploadFile/UploadFile';
// service
import { createBookingDocuments } from '../../../services/bookingDocuments/bookingDocumentsService';

/**
 * Empty document object
 * @type {BookingDocument}
 */
const emptyDoc = {
  type: '',
  file: null,
  fileUuid: null,
  isVisible: true,
  title: '',
  bookings: [],
};

/**
 * Booking details component form for panel
 * @param {object} props
 * @param {number[]} props.bookingIds
 * @param {() => void} props.onSuccess
 * @returns {JSX.Element}
 */
function BookingAddDocument({ bookingIds, onSuccess }) {
  const { t } = useTranslation();

  // local states
  const [documents, setDocuments] = useState([emptyDoc]);

  // mutations
  const {
    mutate,
  } = useMutation(createBookingDocuments);

  const handleAddDocument = () => {
    mutate({ documents, bookingIds }, {
      onSuccess,
    });
  };

  const handleAddNewDocument = () => setDocuments((state) => state.concat(emptyDoc));
  /**
   * Handle type change
   * @param {number} index
   * @param {string} type
   */
  const handleTypeChange = (index, type) => {
    setDocuments((state) => state.map((doc, i) => (i === index ? { ...doc, type } : doc)));
  };

  /**
   * Handle title change
   * @param {number} index
   * @param {string} title
   */
  const handleTitleChange = (index, title) => {
    setDocuments((state) => state.map((doc, i) => (i === index ? { ...doc, title } : doc)));
  };

  /**
   * Handle isVisible value change
   * @param {number} index
   */
  const handleVisibilityChange = (index) => {
    setDocuments((state) => state.map((doc, i) => (i === index
      ? { ...doc, isVisible: !doc?.isVisible } : doc)));
  };

  /**
   * Check if document is complete
   * @param {BookingDocument} doc
   * @returns {boolean} - true if document is complete
   */
  const isComplete = (doc) => !!(doc?.type && doc?.title && doc?.file);

  /**
   * Handle file change
   * @param {number} index
   * @param {File[]} file
   * @param {string} fileUuid
   */
  const handleFileChange = (index, file, fileUuid) => {
    setDocuments((state) => state.map((doc, i) => (i === index
      ? { ...doc, file: file?.[0], fileUuid } : doc)));
  };

  /**
   * Render document section
   * @param {object} param
   * @param {BookingDocument} param.doc
   * @param {number} param.index
   * @returns {JSX.Element}
   */
  const renderDocSection = ({ doc, index }) => (
    <div key={doc.fileUuid} className="d-flex f-column">
      <FormSection className="margin-bottom-s" label={t('bookingAddDocument.fields.docNumber', { nb: index + 1 })}>
        <div className="d-flex f-column gap-s">
          <Select
            options={BOOKING_DOC_TYPES}
              // @ts-ignore
            placeholder={t('bookingAddDocument.fields.type')}
            onChange={
              /**
               * @param {React.ChangeEvent<HTMLSelectElement>} e
               * @returns {void}
               */
              (e) => handleTypeChange(index, e?.target?.value)
            }
            value={doc?.type}
          />
          <Textfield
            // @ts-ignore
            placeholder={t('bookingAddDocument.fields.title')}
            type="text"
            value={doc?.title}
            onChange={
              /**
               * @param {React.ChangeEvent<HTMLInputElement>} e
               * @returns {void}
               */
              (e) => handleTitleChange(index, e.target.value)
            }
          />
          <UploadFile
            hasFile={!!doc.file}
            file={doc.file}
            noFileChildren={t('bookingAddDocument.fields.dropFile')}
            index={index}
            onDrop={(file, fileUuid) => handleFileChange(index, file, fileUuid)}
          />
          <Checkbox
            label={t('bookingAddDocument.fields.isVisible')}
            checked={doc?.isVisible}
            // @ts-ignore
            onChange={() => handleVisibilityChange(index)}
          />
        </div>
      </FormSection>
      {index + 1 === documents?.length && isComplete(doc) ? (
        <Button
          className="justify-end"
          color="neutral00"
          icon="add"
          size="small"
          disabled={!isComplete(doc)}
          onClick={handleAddNewDocument}
        >
          {t('bookingAddDocument.actions.addOther')}
        </Button>
      ) : null}
    </div>
  );

  return (
    <div className="d-flex f-column justify-between full-screen-height">
      <div>
        <div>
          <div className="d-flex f-row f-wrap margin-bottom-m">
            <p className="h1-default margin-right-m">
              {t('bookingAddDocument.title', { count: bookingIds?.length })}
            </p>
          </div>

          { documents.map((doc, index) => renderDocSection({ doc, index })) }
        </div>
      </div>
      <div className="gap-m d-flex f-column margin-top-m padding-bottom-m">
        <Button onClick={handleAddDocument} isLoading={false}>
          {t('bookingAddDocument.actions.add')}
        </Button>
      </div>
    </div>
  );
}

export default BookingAddDocument;
